import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { formatTime } from '../../lib/utils';
import { Car, DollarSign, Moon } from 'lucide-react';

const ShiftNoteFormDialog = ({ shifts, participants, onSave, onClose }) => {
  const [shiftId, setShiftId] = useState('');
  const [participantId, setParticipantId] = useState('');
  const [adlChecks, setAdlChecks] = useState({ showering: false, dressing: false, toileting: false });
  const [mealChecks, setMealChecks] = useState({ breakfast: false, lunch: false, dinner: false });
  const [medsCheck, setMedsCheck] = useState(false);
  const [travelPurpose, setTravelPurpose] = useState('');
  const [overnightReason, setOvernightReason] = useState('');
  const [expenses, setExpenses] = useState([]);

  // Refs for all text inputs — no re-render on typing
  const adlNotesRef = useRef(null);
  const mealNotesRef = useRef(null);
  const medNotesRef = useRef(null);
  const behavioursRef = useRef(null);
  const communityRef = useRef(null);
  const goalsRef = useRef(null);
  const generalRef = useRef(null);
  const travelKmRef = useRef(null);
  const travelStartRef = useRef(null);
  const travelEndRef = useRef(null);
  const overnightHoursRef = useRef(null);
  const overnightNotesRef = useRef(null);
  const expDescRef = useRef(null);
  const expAmountRef = useRef(null);
  const [expReceipt, setExpReceipt] = useState(false);

  const sortedShifts = useMemo(() => {
    return [...shifts].sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  }, [shifts]);

  useEffect(() => {
    if (shifts.length > 0 && !shiftId) {
      const now = new Date();
      const sorted = [...shifts].sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
      const active = sorted.find(s => now >= new Date(s.start_time) && now <= new Date(s.end_time));
      const recent = active || sorted.find(s => {
        const h = (now - new Date(s.end_time)) / 3600000;
        return h >= 0 && h <= 12;
      }) || sorted[0];
      if (recent) setShiftId(recent.id);
    }
  }, [shifts]);

  const collectFormData = useCallback(() => {
    // Auto-capture any pending expense the user typed but didn't click Add
    const allExpenses = [...expenses];
    const pendingDesc = expDescRef.current?.value;
    const pendingAmt = expAmountRef.current?.value;
    if (pendingDesc && pendingAmt) {
      allExpenses.push({ description: pendingDesc, amount: pendingAmt, receipt: expReceipt });
    }

    return {
      shift_id: shiftId,
      participant_id: participantId,
      adls: { ...adlChecks, notes: adlNotesRef.current?.value || '' },
      meals: { ...mealChecks, notes: mealNotesRef.current?.value || '' },
      medications: { administered: medsCheck, notes: medNotesRef.current?.value || '' },
      behaviours: behavioursRef.current?.value || '',
      community_access: communityRef.current?.value || '',
      goals_progress: goalsRef.current?.value || '',
      general_notes: generalRef.current?.value || '',
      is_family_safe: true,
      travel: {
        kilometers: travelKmRef.current?.value || '',
        purpose: travelPurpose,
        start_location: travelStartRef.current?.value || '',
        end_location: travelEndRef.current?.value || '',
      },
      expenses: allExpenses,
      active_overnight: {
        hours: overnightHoursRef.current?.value || '',
        reason: overnightReason,
        notes: overnightNotesRef.current?.value || '',
      },
    };
  }, [shiftId, participantId, adlChecks, mealChecks, medsCheck, travelPurpose, overnightReason, expenses, expReceipt]);

  const handleAddExpense = () => {
    const desc = expDescRef.current?.value;
    const amt = expAmountRef.current?.value;
    if (desc && amt) {
      setExpenses(prev => [...prev, { description: desc, amount: amt, receipt: expReceipt }]);
      expDescRef.current.value = '';
      expAmountRef.current.value = '';
      setExpReceipt(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Create Shift Note</DialogTitle>
      </DialogHeader>
      <div className="space-y-6 py-4">
        <div className="space-y-2">
          <Label>Select Shift *</Label>
          <Select value={shiftId} onValueChange={setShiftId}>
            <SelectTrigger data-testid="note-shift"><SelectValue placeholder="Select a shift" /></SelectTrigger>
            <SelectContent>
              {sortedShifts.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {new Date(s.start_time).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', timeZone: 'Australia/Adelaide' })} - {s.house_name} - {formatTime(s.start_time)}-{formatTime(s.end_time)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Participant *</Label>
          <Select value={participantId} onValueChange={setParticipantId}>
            <SelectTrigger data-testid="note-participant"><SelectValue placeholder="Select participant" /></SelectTrigger>
            <SelectContent>
              {participants.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3 p-4 rounded-lg bg-muted/30">
          <Label className="text-base font-semibold">Activities of Daily Living (ADLs)</Label>
          <div className="flex flex-wrap gap-4">
            {['showering', 'dressing', 'toileting'].map(f => (
              <div key={f} className="flex items-center space-x-2">
                <Checkbox id={f} checked={adlChecks[f]} onCheckedChange={(c) => setAdlChecks(prev => ({ ...prev, [f]: c }))} />
                <label htmlFor={f} className="text-sm capitalize">{f}</label>
              </div>
            ))}
          </div>
          <Input ref={adlNotesRef} placeholder="Additional ADL notes..." defaultValue="" />
        </div>

        <div className="space-y-3 p-4 rounded-lg bg-muted/30">
          <Label className="text-base font-semibold">Meals</Label>
          <div className="flex flex-wrap gap-4">
            {['breakfast', 'lunch', 'dinner'].map(f => (
              <div key={f} className="flex items-center space-x-2">
                <Checkbox id={`meal-${f}`} checked={mealChecks[f]} onCheckedChange={(c) => setMealChecks(prev => ({ ...prev, [f]: c }))} />
                <label htmlFor={`meal-${f}`} className="text-sm capitalize">{f}</label>
              </div>
            ))}
          </div>
          <Input ref={mealNotesRef} placeholder="Meal notes..." defaultValue="" />
        </div>

        <div className="space-y-3 p-4 rounded-lg bg-muted/30">
          <Label className="text-base font-semibold">Medications</Label>
          <div className="flex items-center space-x-2">
            <Checkbox id="meds" checked={medsCheck} onCheckedChange={setMedsCheck} />
            <label htmlFor="meds" className="text-sm">Medications Administered</label>
          </div>
          <Input ref={medNotesRef} placeholder="Medication notes..." defaultValue="" />
        </div>

        <div className="space-y-2">
          <Label>Behaviours Observed</Label>
          <Textarea ref={behavioursRef} defaultValue="" rows={2} />
        </div>
        <div className="space-y-2">
          <Label>Community Access</Label>
          <Textarea ref={communityRef} defaultValue="" rows={2} />
        </div>
        <div className="space-y-2">
          <Label>Goals Progress</Label>
          <Textarea ref={goalsRef} defaultValue="" rows={2} />
        </div>

        <div className="space-y-3 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
          <Label className="text-base font-semibold flex items-center gap-2"><Car className="h-4 w-4 text-blue-500" />Travel</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Kilometers</Label>
              <Input ref={travelKmRef} type="number" step="0.1" placeholder="0.0" defaultValue="" data-testid="travel-km" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Purpose</Label>
              <Select value={travelPurpose} onValueChange={setTravelPurpose}>
                <SelectTrigger data-testid="travel-purpose"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="community_access">Community Access</SelectItem>
                  <SelectItem value="medical_appointment">Medical Appointment</SelectItem>
                  <SelectItem value="shopping">Shopping</SelectItem>
                  <SelectItem value="social_activity">Social Activity</SelectItem>
                  <SelectItem value="work_travel">Between Sites</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Start Location</Label>
              <Input ref={travelStartRef} placeholder="e.g., Sunrise House" defaultValue="" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">End Location</Label>
              <Input ref={travelEndRef} placeholder="e.g., Shopping Centre" defaultValue="" />
            </div>
          </div>
        </div>

        <div className="space-y-3 p-4 rounded-lg bg-green-500/5 border border-green-500/20">
          <Label className="text-base font-semibold flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-500" />Expenses</Label>
          {expenses.length > 0 && (
            <div className="space-y-2">
              {expenses.map((expense, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 rounded bg-background/50 border border-border/40">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">{expense.description}</span>
                    <span className="text-sm text-muted-foreground">${parseFloat(expense.amount).toFixed(2)}</span>
                    {expense.receipt && <span className="text-xs bg-green-500/10 text-green-600 px-2 py-0.5 rounded">Receipt</span>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setExpenses(prev => { const u = [...prev]; u.splice(idx, 1); return u; })}>Remove</Button>
                </div>
              ))}
              <div className="text-sm font-medium text-right">Total: ${expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0).toFixed(2)}</div>
            </div>
          )}
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5 space-y-1">
              <Label className="text-xs">Description</Label>
              <Input ref={expDescRef} placeholder="e.g., Lunch, Taxi" defaultValue="" data-testid="expense-desc" />
            </div>
            <div className="col-span-3 space-y-1">
              <Label className="text-xs">Amount ($)</Label>
              <Input ref={expAmountRef} type="number" step="0.01" placeholder="0.00" defaultValue="" data-testid="expense-amount" />
            </div>
            <div className="col-span-2 flex items-center gap-1 pb-2">
              <Checkbox id="has-receipt" checked={expReceipt} onCheckedChange={setExpReceipt} />
              <label htmlFor="has-receipt" className="text-xs">Receipt</label>
            </div>
            <div className="col-span-2">
              <Button type="button" variant="outline" size="sm" className="w-full" onClick={handleAddExpense} data-testid="add-expense-btn">Add</Button>
            </div>
          </div>
        </div>

        <div className="space-y-3 p-4 rounded-lg bg-indigo-500/5 border border-indigo-500/20">
          <Label className="text-base font-semibold flex items-center gap-2"><Moon className="h-4 w-4 text-indigo-500" />Active Overnight Hours</Label>
          <p className="text-xs text-muted-foreground">Record any hours worked actively during a sleepover shift</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Hours Active</Label>
              <Input ref={overnightHoursRef} type="number" step="0.5" min="0" max="10" placeholder="0.0" defaultValue="" data-testid="active-overnight-hours" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Reason</Label>
              <Select value={overnightReason} onValueChange={setOvernightReason}>
                <SelectTrigger data-testid="active-overnight-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="participant_unwell">Participant Unwell</SelectItem>
                  <SelectItem value="behaviour_support">Behaviour Support</SelectItem>
                  <SelectItem value="personal_care">Personal Care Needs</SelectItem>
                  <SelectItem value="medication">Medication Administration</SelectItem>
                  <SelectItem value="emergency">Emergency Response</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Textarea ref={overnightNotesRef} placeholder="Additional details..." defaultValue="" rows={2} />
        </div>

        <div className="space-y-2">
          <Label>General Notes</Label>
          <Textarea ref={generalRef} defaultValue="" placeholder="Any other observations or notes..." rows={3} data-testid="note-general" />
        </div>
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button variant="secondary" onClick={() => onSave(collectFormData(), true)} data-testid="save-draft-btn">Save Draft</Button>
        <Button onClick={() => onSave(collectFormData(), false)} data-testid="submit-note-btn">Submit</Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default React.memo(ShiftNoteFormDialog);
